import {Component, OnInit} from "@angular/core";
import {AuthService} from "../../core/services/auth.service";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {Router} from "@angular/router";
import {LoginService} from "../../core/services/login.service";
import {NotificationService} from "../../core/services/notification.service";
import {NotifyParentService} from "../../core/services/notify-parent.service";
import {MixpanelService} from "../../core/services/mixpanel.service";
import {CustomValidators} from "../../core/helpers/custom-validators";
import {ValidationHelperService} from "../../core/helpers/validation-helper.service";
import {LocationsControllerService} from "../../core/api/controllers/locations-controller.service";
import {AuthControllerService} from "../../core/api/controllers/auth-controller/auth-controller.service";
import {ILoginError} from "../../core/api/controllers/auth-controller/data/login-error.interface";

@Component({
  selector: "app-login",
  templateUrl: "./login.component.html",
  styleUrls: ["./login.component.scss"],
  providers: [LoginService],
})
export class LoginComponent implements OnInit {
  loginForm: FormGroup;
  codeForm: FormGroup;
  tfa = null;
  codeTry = "";
  timeoutValue = 900; // in seconds
  timeoutInfo = {
    loginAttempts: 0,
    timeoutStart: 0,
  };

  isLoading = false;

  constructor(
    private authService: AuthService,
    private loginService: LoginService,
    private authController: AuthControllerService,
    private formBuilder: FormBuilder,
    private router: Router,
    private notificationService: NotificationService,
    private notifyParent: NotifyParentService,
    private mixpanelService: MixpanelService,
    private locationsController: LocationsControllerService
  ) {}

  ngOnInit() {
    this.tfa =
      this.authService.checkTokenAccess("PRE_AUTH") ||
      this.authService.checkTokenAccess("ADMIN_PRE_AUTH");
    this.loginForm = this.formBuilder.group({
      loginUsername: ["", [Validators.required]],
      loginPassword: ["", [Validators.required]],
      rememberMe: [false],
    });
    this.codeForm = this.formBuilder.group({
      code: ["", [Validators.required, CustomValidators.codeValidator]],
    });
  }

  login() {
    if (!this.loginForm.valid) {
      this.loginForm.markAllAsTouched();
      return;
    }
    this.makeLoginCall();
  }

  makeLoginCall(): void {
    this.mixpanelService.track("login", {
      username: this.loginForm.controls["loginUsername"].value,
    });
    this.isLoading = true;
    this.authController
      .login(
        this.loginForm.controls["loginUsername"].value,
        this.loginForm.controls["loginPassword"].value,
      )
      .then((response) => {
        this.loginService.setTokens(
          this.loginForm.controls["rememberMe"].value,
          response["accessToken"],
          response["refreshToken"],
        );
        this.tfa =
          this.authService.checkTokenAccess("PRE_AUTH") ||
          this.authService.checkTokenAccess("ADMIN_PRE_AUTH");
        this.mixpanelService.track("login_success", {
          username: this.loginForm.controls["loginUsername"].value,
        });

        if (!this.tfa) {
          this.router.navigate(["/"]);
          this.notifyParent.sendData("fullUpdate");
        }

        if (response.selectedLocationId) {
          AuthService.setSelectedLocationId(response.selectedLocationId)
        } else {

        }
        this.isLoading = false;
      })
        .catch((error: ILoginError) => {
          AuthService.removeOauthTokenFromStorage();
          this.timeoutInfo.loginAttempts++;

          let lockout = error?.error?.errorData?.LockoutExpiresAt;
          if (lockout && !lockout.endsWith("Z")) {
            lockout += "Z";
          }

          if (lockout) {
            const unlockTime = Date.parse(lockout);
            const now = new Date().getTime();
            let msLeft = unlockTime - now;

            if (msLeft > 0) {
              const minutes = Math.floor(msLeft / 60000);
              const seconds = Math.floor((msLeft % 60000) / 1000);
              this.notificationService.error(
                  `Too many failed attempts. Try again in ${minutes} minute${minutes !== 1 ? "s" : ""} and ${seconds} second${seconds !== 1 ? "s" : ""}.`
              );
            } else {
              this.notificationService.error(error?.error?.error ?? "Username or password is incorrect");
            }
          } else {
            this.notificationService.error(error?.error?.error ?? "Username or password is incorrect");
          }

          this.isLoading = false;
        });

  }

  login2FA() {
    this.loginService
      .login2FA(
        this.loginForm.controls["loginUsername"].value,
        this.loginForm.controls["loginPassword"].value,
        this.codeTry,
      )
      .then((response) => {
        this.loginService.setTokens(
          this.loginForm.controls["rememberMe"].value,
          response["access_token"],
          response["refresh_token"],
        );
        this.router.navigate(["/"]);
        this.notifyParent.sendData("fullUpdate");
      })
      .catch((error) => this.notificationService.error(error));
  }

  logout() {
    this.mixpanelService.track("logout", {});
    this.authService.logout();
  }

  get currentTimeoutMinute(): number {
    return Math.floor(this.currentTimeout / 60) + 1;
  }

  get currentTimeoutSecond(): number {
    return Math.floor(this.currentTimeout % 60);
  }

  get currentTimeout(): number {
    if (this.timeoutInfo.timeoutStart === 0) return 0;
    const cTimeout =
      this.timeoutValue -
      (new Date().getTime() - this.timeoutInfo.timeoutStart) / 1000;
    return cTimeout > 0 ? cTimeout : 0;
  }

  get loggedIn() {
    return this.authService.loggedIn();
  }

  get fullyLoggedIn(): boolean {
    return (
      this.authService.checkTokenAccess("User") ||
      this.authService.checkTokenAccess("OrganizationAdmin")
    );
  }

  protected readonly ValidationHelperService = ValidationHelperService;
}
